import { useCallback, useEffect, useRef, useState } from "react";
import { STICKY_BANNER_RULE_TYPES } from "../configs/stickyBanner/stickyBannerConfig.types";

function clamp01(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

function getDocumentScrollRatio() {
  const el = document.documentElement;
  const maxScroll = el.scrollHeight - window.innerHeight;
  if (maxScroll <= 0) return 0;
  return clamp01(window.scrollY / maxScroll);
}

/**
 * Stable 3-phase scroll messaging for REPORT milestone mode:
 * phase 1 = top, phase 2 = middle, phase 3 = bottom.
 *
 * Middle detection (OR):
 * - Option A: IntersectionObserver on `middleMilestoneTargetId` or first SCROLL_ELEMENT milestone target.
 * - Option B: Scroll ratio band `middleScrollEnterRatio` / `middleScrollExitRatio` (hysteresis) so middle
 *   always activates even if the DOM node is missing or IO is strict.
 *
 * Bottom: scroll ratio with enter/exit hysteresis from SCROLL_PERCENT milestone or commonDefaults /
 * `bottomThreshold`.
 *
 * React state updates only when the phase integer changes.
 */
export function useStickyBannerMilestonePhase({ milestones, enabled, commonDefaults }) {
  const [phase, setPhase] = useState(1);
  const phaseRef = useRef(1);
  const middleIoActiveRef = useRef(false);
  const middleScrollActiveRef = useRef(false);
  const bottomActiveRef = useRef(false);

  const publishPhase = useCallback(() => {
    const milestonesList = milestones ?? [];
    const elementMilestone = milestonesList.find(
      (m) => m?.type === STICKY_BANNER_RULE_TYPES.SCROLL_ELEMENT && m.targetElementId
    );
    const percentMilestone = milestonesList.find((m) => m?.type === STICKY_BANNER_RULE_TYPES.SCROLL_PERCENT);

    const bottomEnter =
      percentMilestone?.enterRatio ??
      commonDefaults?.bottomThreshold ??
      commonDefaults?.scrollBottomEnterRatio ??
      0.86;
    const bottomExit =
      percentMilestone?.exitRatio ?? commonDefaults?.scrollBottomExitRatio ?? Math.max(0, bottomEnter - 0.08);

    const middleOrder = elementMilestone?.order ?? 2;
    const bottomOrder = percentMilestone?.order ?? 3;

    const middleActive = middleIoActiveRef.current || middleScrollActiveRef.current;

    let next = 1;
    if (bottomActiveRef.current) {
      next = bottomOrder;
    } else if (middleActive && (elementMilestone || commonDefaults?.middleMilestoneTargetId)) {
      next = middleOrder;
    } else if (middleActive) {
      next = 2;
    }

    if (next !== phaseRef.current) {
      phaseRef.current = next;
      setPhase(next);
    }
  }, [milestones, commonDefaults]);

  useEffect(() => {
    if (!enabled || !milestones?.length) {
      middleIoActiveRef.current = false;
      middleScrollActiveRef.current = false;
      bottomActiveRef.current = false;
      if (phaseRef.current !== 1) {
        phaseRef.current = 1;
        setPhase(1);
      }
      return undefined;
    }

    const milestonesList = milestones;
    const elementMilestone = milestonesList.find(
      (m) => m?.type === STICKY_BANNER_RULE_TYPES.SCROLL_ELEMENT && m.targetElementId
    );
    const percentMilestone = milestonesList.find((m) => m?.type === STICKY_BANNER_RULE_TYPES.SCROLL_PERCENT);

    const middleTargetId =
      (typeof commonDefaults?.middleMilestoneTargetId === "string" && commonDefaults.middleMilestoneTargetId.trim()) ||
      elementMilestone?.targetElementId ||
      null;

    const middleEnter = commonDefaults?.middleScrollEnterRatio ?? 0.35;
    const middleExit = commonDefaults?.middleScrollExitRatio ?? 0.3;

    const bottomEnter =
      percentMilestone?.enterRatio ??
      commonDefaults?.bottomThreshold ??
      commonDefaults?.scrollBottomEnterRatio ??
      0.86;
    const bottomExit =
      percentMilestone?.exitRatio ?? commonDefaults?.scrollBottomExitRatio ?? Math.max(0, bottomEnter - 0.08);

    let middleObserver = null;
    let observedMiddleNode = null;

    const disconnectMiddleObserver = () => {
      if (middleObserver) {
        middleObserver.disconnect();
        middleObserver = null;
        observedMiddleNode = null;
      }
    };

    const bindMiddleObserver = () => {
      if (!middleTargetId) {
        disconnectMiddleObserver();
        middleIoActiveRef.current = false;
        publishPhase();
        return;
      }

      const node = document.getElementById(middleTargetId);
      if (!node) {
        disconnectMiddleObserver();
        middleIoActiveRef.current = false;
        publishPhase();
        return;
      }

      if (observedMiddleNode === node && middleObserver) {
        publishPhase();
        return;
      }

      disconnectMiddleObserver();

      const th = typeof elementMilestone?.threshold === "number" ? elementMilestone.threshold : 0.05;
      const rootMargin = elementMilestone?.rootMargin ?? "0px 0px -12% 0px";

      middleObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          const relaxed =
            entry.isIntersecting && (entry.intersectionRatio >= th || entry.intersectionRatio > 0.02);
          middleIoActiveRef.current = relaxed;
          publishPhase();
        },
        { root: null, rootMargin, threshold: [0, 0.02, 0.05, th, 0.15, 0.35, 0.6, 1] }
      );
      middleObserver.observe(node);
      observedMiddleNode = node;
      publishPhase();
    };

    bindMiddleObserver();

    let raf = 0;
    const onScrollOrResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const ratio = getDocumentScrollRatio();

        const wasBottom = bottomActiveRef.current;
        if (!wasBottom && ratio >= bottomEnter) bottomActiveRef.current = true;
        else if (wasBottom && ratio < bottomExit) bottomActiveRef.current = false;

        const wasMiddleScroll = middleScrollActiveRef.current;
        if (!wasMiddleScroll && ratio >= middleEnter) middleScrollActiveRef.current = true;
        else if (wasMiddleScroll && ratio < middleExit) middleScrollActiveRef.current = false;

        if (middleTargetId) {
          const node = document.getElementById(middleTargetId);
          if (node && node !== observedMiddleNode) {
            bindMiddleObserver();
          }
        }

        publishPhase();
      });
    };

    onScrollOrResize();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize, { passive: true });

    const retryId = requestAnimationFrame(() => {
      bindMiddleObserver();
      onScrollOrResize();
    });

    return () => {
      cancelAnimationFrame(retryId);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      if (raf) cancelAnimationFrame(raf);
      disconnectMiddleObserver();
    };
  }, [enabled, milestones, publishPhase, commonDefaults]);

  return phase;
}
